import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { X, Download, UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy } from 'lucide-react';
import {
  downloadPatientSampleExcel,
  checkPatientBulkFormat,
  bulkUploadPatients,
} from '../../redux/patientSlice';

// Reusable bulk-upload modal for patients. Used by both admin and doctor patient
// pages. When `doctorId` is passed (doctor uploads), every created patient links
// to that doctor; admins leave it empty and use the per-row DoctorEmail column.
//
// Mirrors the Country/Support bulk-upload UX: download sample → pick file →
// format check → upload → per-row created/duplicates/errors summary.
export default function BulkUploadPatients({ isOpen, onClose, doctorId, onComplete }) {
  const { t } = useTranslation(['patientpage']);
  const dispatch = useDispatch();

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [checking, setChecking] = useState(false);
  const [formatValid, setFormatValid] = useState(false);
  const [formatInfo, setFormatInfo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const reset = () => {
    setFile(null);
    setFileError('');
    setChecking(false);
    setFormatValid(false);
    setFormatInfo(null);
    setUploading(false);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownloadSample = async () => {
    setDownloading(true);
    try {
      await dispatch(downloadPatientSampleExcel()).unwrap();
    } catch (err) {
      toast.warning(err?.error || t('patientpage.bulk_download_failed', 'Failed to download sample'), { autoClose: 2500 });
    } finally {
      setDownloading(false);
    }
  };

  const validateAndCheck = async (selected) => {
    setResult(null);
    setFormatValid(false);
    setFormatInfo(null);
    setFileError('');

    const ext = (selected.name.split('.').pop() || '').toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      setFile(null);
      setFileError(t('patientpage.bulk_invalid_type', 'Only Excel files (.xlsx, .xls) are allowed'));
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      setFile(null);
      setFileError(t('patientpage.bulk_too_large', 'File is too large (max 5MB)'));
      return;
    }

    setFile(selected);
    setChecking(true);
    try {
      const res = await dispatch(checkPatientBulkFormat(selected)).unwrap();
      setFormatValid(true);
      setFormatInfo(res);
    } catch (err) {
      setFormatValid(false);
      setFileError(err?.error || t('patientpage.bulk_bad_format', 'Invalid Excel format'));
    } finally {
      setChecking(false);
    }
  };

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (selected) validateAndCheck(selected);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const selected = e.dataTransfer.files?.[0];
    if (selected) validateAndCheck(selected);
  };

  const handleUpload = async () => {
    if (!file || !formatValid) return;
    setUploading(true);
    try {
      const res = await dispatch(bulkUploadPatients({ file, doctorId })).unwrap();
      setResult(res);
      const created = res?.summary?.totalCreated || 0;
      toast.success(
        t('patientpage.bulk_done', '{{n}} patient(s) created', { n: created }),
        { autoClose: 2500 }
      );
      if (onComplete) onComplete();
    } catch (err) {
      toast.warning(err?.error || t('patientpage.bulk_upload_failed', 'Bulk upload failed'), { autoClose: 2500 });
    } finally {
      setUploading(false);
    }
  };

  const summary = result?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#3AA8A8]" />
            <h2 className="text-lg font-bold text-gray-800">
              {t('patientpage.bulk_title', 'Bulk Upload Patients')}
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Step 1: sample */}
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-600 mb-3">
              {t('patientpage.bulk_step1', 'Download the template, fill in patient rows (Name & Email are required), then upload it. Each patient is emailed a temporary password and must verify by OTP on first sign-in.')}
            </p>
            <button
              onClick={handleDownloadSample}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#3AA8A8] font-semibold hover:bg-[#ecfdfd] transition text-sm disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
              {downloading ? t('patientpage.bulk_downloading', 'Downloading…') : t('patientpage.bulk_download', 'Download Sample Excel')}
            </button>
          </div>

          {/* Step 2: file picker */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="rounded-xl border-2 border-dashed border-gray-300 p-6 text-center hover:border-[#4ABDBD] transition"
          >
            <UploadCloud className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <label className="cursor-pointer">
              <span className="text-[#3AA8A8] font-semibold underline">{t('patientpage.bulk_choose', 'Choose a file')}</span>
              <span className="text-gray-500 text-sm"> {t('patientpage.bulk_or_drag', 'or drag and drop (.xlsx, .xls, max 5MB)')}</span>
              <input type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
            </label>
            {file && (
              <div className="mt-3 text-sm text-gray-700 flex items-center justify-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-[#3AA8A8]" />
                {file.name}
              </div>
            )}
            {checking && <div className="mt-2 text-xs text-gray-500">{t('patientpage.bulk_checking', 'Checking format…')}</div>}
            {formatValid && formatInfo && (
              <div className="mt-2 text-xs text-green-600 flex items-center justify-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                {t('patientpage.bulk_format_ok', 'Format OK — {{n}} data row(s) detected', { n: formatInfo.dataRowCount || 0 })}
              </div>
            )}
            {fileError && (
              <div className="mt-2 text-xs text-red-600 flex items-center justify-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {fileError}
              </div>
            )}
          </div>

          {/* Result summary */}
          {summary && (
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-green-50 p-3">
                  <div className="text-2xl font-bold text-green-600">{summary.totalCreated}</div>
                  <div className="text-xs text-gray-500">{t('patientpage.bulk_created', 'Created')}</div>
                </div>
                <div className="rounded-lg bg-amber-50 p-3">
                  <div className="text-2xl font-bold text-amber-600">{summary.totalDuplicates}</div>
                  <div className="text-xs text-gray-500">{t('patientpage.bulk_duplicates', 'Duplicates')}</div>
                </div>
                <div className="rounded-lg bg-red-50 p-3">
                  <div className="text-2xl font-bold text-red-600">{summary.totalErrors}</div>
                  <div className="text-xs text-gray-500">{t('patientpage.bulk_errors', 'Errors')}</div>
                </div>
              </div>

              {(result.duplicates?.length > 0 || result.errors?.length > 0) && (
                <div className="mt-4 max-h-40 overflow-y-auto text-xs space-y-1">
                  {result.duplicates?.map((d, i) => (
                    <div key={`dup-${i}`} className="flex items-center gap-2 text-amber-700">
                      <Copy className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{t('patientpage.bulk_row', 'Row')} {d.row}: {d.email} — {d.reason}</span>
                    </div>
                  ))}
                  {result.errors?.map((e, i) => (
                    <div key={`err-${i}`} className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{t('patientpage.bulk_row', 'Row')} {e.row}: {e.email} — {e.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
          <button onClick={handleClose} className="px-4 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-semibold">
            {result ? t('patientpage.bulk_close', 'Close') : t('patientpage.bulk_cancel', 'Cancel')}
          </button>
          {!result && (
            <button
              onClick={handleUpload}
              disabled={!formatValid || uploading || checking}
              className={`px-4 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] ${(!formatValid || uploading || checking) ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg'}`}
            >
              {uploading ? t('patientpage.bulk_uploading', 'Uploading…') : t('patientpage.bulk_upload', 'Upload')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
